import { normalizeEmailThread } from "./adminEmailThreadNormalize";

/**
 * Client-visible email pipeline for a service-request **list** row.
 *
 * **Preferred:** API sets **`emailStage`** or **`clientEmailStage`** on each item (see `docs/frontend-admin-email-api.md`).
 *
 * **Otherwise** the SPA derives from **`emailMessages`** / **`emailThread`**, or from optional counts
 * **`outboundClientEmailCount`** / **`inboundClientEmailCount`** when the list is lean.
 *
 * Optional **`outboundEmailPending`** (aliases **`clientEmailSendPending`**, **`clientEmailOutboundPending`**) or
 * **`emailStage`: `sending`** — shows **Sending…** next to **Not sent** while SMTP has not persisted an outbound row yet.
 *
 * @typedef {'none' | 'not_sent' | 'sent' | 'client_replied'} ClientEmailStage
 */

/** @type {Record<string, ClientEmailStage>} */
const API_STAGE_MAP = {
  none: "none",
  not_sent: "not_sent",
  no_outbound: "not_sent",
  awaiting_send: "not_sent",
  sent: "sent",
  outbound_sent: "sent",
  awaiting_reply: "sent",
  awaiting_client: "sent",
  client_replied: "client_replied",
  has_inbound: "client_replied",
  replied: "client_replied",
  in_thread: "client_replied",
};

/**
 * @param {unknown} raw
 * @returns {ClientEmailStage | null} null = not provided / invalid
 */
function parseApiEmailStage(raw) {
  if (raw == null || raw === "") return null;
  const key = String(raw).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (key === "sending" || key === "outbound_pending") return null;
  return API_STAGE_MAP[key] ?? null;
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 */
function outboundPendingFlagsOnly(row) {
  if (!row || typeof row !== "object") return false;
  return (
    row.outboundEmailPending === true ||
    row.clientEmailSendPending === true ||
    row.clientEmailOutboundPending === true
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 */
function emailStageKeywordSending(row) {
  if (!row || typeof row !== "object") return false;
  const key = String(row.emailStage ?? row.clientEmailStage ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return key === "sending" || key === "outbound_pending";
}

/**
 * @param {number} out
 * @param {number} inn
 * @returns {ClientEmailStage}
 */
function stageFromCounts(out, inn) {
  const o = Math.max(0, Math.floor(Number(out) || 0));
  const i = Math.max(0, Math.floor(Number(inn) || 0));
  if (o === 0 && i === 0) return "not_sent";
  if (o > 0 && i === 0) return "sent";
  return "client_replied";
}

/**
 * @param {{ stage: ClientEmailStage, source: 'api' | 'counts' | 'thread' | 'unknown', title: string }} base
 * @param {Record<string, unknown> | null | undefined} row
 */
function withOutboundPendingChip(base, row) {
  const flags = outboundPendingFlagsOnly(row);
  const show = base.stage === "not_sent" && flags;
  const titleExtra = show
    ? " Outbound queued — SMTP not finished yet (no row in thread)."
    : "";
  return {
    ...base,
    outboundPending: show,
    title: base.title + titleExtra,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {{ stage: ClientEmailStage, source: 'api' | 'counts' | 'thread' | 'unknown', title: string, outboundPending?: boolean }}
 */
export function getServiceRequestListClientEmailStage(row) {
  if (!row || typeof row !== "object") {
    return {
      stage: "none",
      source: "unknown",
      title: "No email thread data on this list row.",
      outboundPending: false,
    };
  }

  if (emailStageKeywordSending(row)) {
    return {
      stage: "not_sent",
      source: "api",
      title:
        emailStageTitle("not_sent", "api") +
        " Outbound email queued — SMTP not finished yet.",
      outboundPending: true,
    };
  }

  const apiRaw = row.emailStage ?? row.clientEmailStage;
  const parsed = parseApiEmailStage(apiRaw);
  if (parsed) {
    return withOutboundPendingChip(
      {
        stage: parsed,
        source: "api",
        title: emailStageTitle(parsed, "api"),
      },
      row
    );
  }

  const hasCount =
    row.outboundClientEmailCount != null ||
    row.inboundClientEmailCount != null ||
    row.outboundEmailCount != null ||
    row.inboundEmailCount != null;
  if (hasCount) {
    const out =
      row.outboundClientEmailCount ?? row.outboundEmailCount ?? 0;
    const inn =
      row.inboundClientEmailCount ?? row.inboundEmailCount ?? 0;
    const stage = stageFromCounts(out, inn);
    return withOutboundPendingChip(
      {
        stage,
        source: "counts",
        title: emailStageTitle(stage, "counts", { out, inn }),
      },
      row
    );
  }

  const raw = row.emailMessages ?? row.emailThread;
  if (!Array.isArray(raw)) {
    return withOutboundPendingChip(
      {
        stage: "none",
        source: "unknown",
        title:
          "Add emailStage or emailMessages to the list API to show client email progress here.",
      },
      row
    );
  }

  if (raw.length === 0) {
    return withOutboundPendingChip(
      {
        stage: "not_sent",
        source: "thread",
        title: emailStageTitle("not_sent", "thread"),
      },
      row
    );
  }

  const thread = normalizeEmailThread(raw, row.email ?? "");
  const out = thread.filter((m) => m.direction === "outbound").length;
  const inn = thread.filter((m) => m.direction === "inbound").length;
  const stage = stageFromCounts(out, inn);
  return withOutboundPendingChip(
    {
      stage,
      source: "thread",
      title: emailStageTitle(stage, "thread", { out, inn }),
    },
    row
  );
}

/**
 * @param {ClientEmailStage} stage
 * @param {'api' | 'counts' | 'thread' | 'unknown'} source
 * @param {{ out?: number, inn?: number }} [extra]
 */
function emailStageTitle(stage, source, extra = {}) {
  const bits = [];
  if (extra.out != null && extra.inn != null) {
    bits.push(`${extra.out} outbound · ${extra.inn} inbound`);
  }
  if (source === "api") bits.push("From API emailStage");
  else if (source === "counts") bits.push("From outbound/inbound counts");
  else if (source === "thread") bits.push("From emailMessages on this row");

  const hint =
    stage === "none"
      ? "Unknown — list row has no thread or stage fields."
      : stage === "not_sent"
        ? "No client-visible outbound email recorded yet."
        : stage === "sent"
          ? "At least one email sent to the client; no inbound reply in thread yet."
          : "Client has at least one inbound message in the thread.";

  return [hint, bits.join(" · ")].filter(Boolean).join(" ");
}

/**
 * @param {ClientEmailStage} stage
 */
export function shortLabelForClientEmailStage(stage) {
  switch (stage) {
    case "not_sent":
      return "Not sent";
    case "sent":
      return "Sent";
    case "client_replied":
      return "Reply";
    default:
      return "—";
  }
}
